import React from 'react';

/**
 * Renders text with highlighted segments from Elasticsearch highlight data.
 * Converts ES highlight tags (<em></em>) to React elements with styling.
 * 
 * @param text - Original text to display
 * @param highlightData - ES highlight object (e.g., { title: ["<em>query</em>..."], content: [...] })
 * @param field - Field name to extract highlights from
 * @param fallbackLength - Max length to show if no highlights (default: 150)
 * @returns React element with highlighted text
 */
export function renderHighlightedText(
  text: string | undefined | null,
  highlightData: Record<string, string[]> | undefined,
  field: string,
  fallbackLength: number = 150
): React.ReactNode {
  // No text to display
  if (!text) return null;

  // No highlights available, return truncated plain text
  if (!highlightData || !highlightData[field] || highlightData[field].length === 0) {
    const truncated = text.length > fallbackLength 
      ? `${text.substring(0, fallbackLength)}...` 
      : text;
    return <span>{truncated}</span>;
  }

  // Get first highlight snippet
  const highlighted = highlightData[field][0];

  // Parse and render with highlights
  return parseHighlightedHtml(highlighted);
}

/**
 * Parses ES highlight HTML and converts to React elements.
 * Replaces <em></em> tags with styled spans.
 * 
 * @param html - HTML string with <em> tags from Elasticsearch
 * @returns React element with styled highlights
 */
function parseHighlightedHtml(html: string): React.ReactNode {
  if (!html) return null;

  // Split by <em> and </em> tags
  const parts: React.ReactNode[] = [];
  const regex = /<em>(.*?)<\/em>/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(html)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {html.substring(lastIndex, match.index)}
        </span>
      );
    }

    // Add highlighted text
    parts.push(
      <mark
        key={`highlight-${match.index}`}
        className="bg-yellow-200 text-gray-900 font-medium rounded px-0.5"
      >
        {match[1]}
      </mark>
    );

    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < html.length) {
    parts.push(
      <span key={`text-${lastIndex}`}>
        {html.substring(lastIndex)}
      </span>
    );
  }

  return <>{parts}</>;
}

/**
 * Extracts plain text from highlight snippet, removing <em> tags.
 * Useful for accessibility or plain-text contexts.
 * 
 * @param highlightData - ES highlight object
 * @param field - Field name to extract from
 * @returns Plain text snippet
 */
export function extractPlainHighlight(
  highlightData: Record<string, string[]> | undefined,
  field: string
): string | null {
  if (!highlightData || !highlightData[field] || highlightData[field].length === 0) {
    return null;
  }

  const highlighted = highlightData[field][0];
  return highlighted.replace(/<\/?em>/g, '');
}

/**
 * Merges highlights from multiple fields into a single snippet.
 * Useful for showing combined title + content highlights.
 * 
 * @param highlightData - ES highlight object
 * @param fields - Array of field names to merge
 * @param separator - String to join snippets (default: " ... ")
 * @returns Combined highlight snippet
 */
export function mergeHighlights(
  highlightData: Record<string, string[]> | undefined,
  fields: string[],
  separator: string = ' ... '
): React.ReactNode {
  if (!highlightData) return null;

  const snippets: React.ReactNode[] = [];

  fields.forEach((field, index) => {
    if (highlightData[field] && highlightData[field].length > 0) {
      const parsed = parseHighlightedHtml(highlightData[field][0]);
      if (parsed) {
        if (snippets.length > 0) {
          snippets.push(<span key={`sep-${index}`}>{separator}</span>);
        }
        snippets.push(<span key={`field-${index}`}>{parsed}</span>);
      }
    }
  });

  return snippets.length > 0 ? <>{snippets}</> : null;
}
